import { http } from '../../../configuration/api';
import {
	APIResponse,
	CreateOfferRequest,
	CreateOfferResponse,
	CropDataResponse,
	LanguageDataResponse,
	OfferDetails,
	OfferDetailsResponse,
	OffersListResponse,
	OfferStatus,
	RequestDetailResponse,
	RequestType,
	SeasonalOneTimeOffer,
	SendOfferRequest,
	UserDataResponse,
} from '../types';

export const getOffers = async (
	status: OfferStatus = 'active',
	page: number = 1,
	limit: number = 10
): Promise<OffersListResponse> => {
	try {
		console.log(`[API] Fetching seasonal/onetime offers with status=${status}, page=${page}`);
		
		// Use the real API endpoint for one-time and seasonal offers
		const response = await http.get<APIResponse>(
			'requests',
			`service-requests/one_time_and_seasonal?page=${page}&limit=${limit}&status=${status}`
		);

		console.log('[API] Response received:', response.data);

		// Check if response has the expected structure
		if (!response.data || !response.data.data) {
			console.error('[API] Invalid response structure:', response);
			throw new Error('Invalid API response structure');
		}

		// Map the API response to our internal data structure
		return {
			data: (response.data.data.data || []).map((item: any) => {
				// Get price from different possible sources and ensure it's a valid number
				let itemPrice = 0;
				if (item.services?.price && !isNaN(parseFloat(item.services.price))) {
					itemPrice = parseFloat(item.services.price);
				} else if (item.price && !isNaN(parseFloat(item.price))) {
					itemPrice = parseFloat(item.price);
				} else if (item.total_price && !isNaN(parseFloat(item.total_price))) {
					itemPrice = parseFloat(item.total_price);
				}
				
				// Log each item's important dates for debugging
				console.log(`[API] Item ${item.id} dates:`);
				console.log(`- start_date: ${item.start_date}`);
				console.log(`- end_date: ${item.end_date}`);
				console.log(`- created_at: ${item.created_at}`);
				console.log(`- payment_status: ${item.payment_status}`);
				
				return {
					...item,
					is_paid: item.payment_status === 'paid' || item.payment_status === 'stripe_payed',
					payment_status: item.payment_status,
					request_active_days: String(item.request_active_days || '3'),
					// Use the properly parsed price
					price: itemPrice,
					currency: item.services?.currency || item.currency || 'EUR'
				};
			}),
			pagination: {
				total_pages: response.data.data.totalPages || 1,
				current_page: response.data.data.currentPage || 1,
				items_per_page: limit,
				total_items: response.data.data.totalData || 0,
			},
		};
	} catch (error: any) {
		console.error('[API] Error fetching seasonal/onetime offers:', error);
		throw error;
	}
};

export const getRequestDetailById = async (requestId: string): Promise<SeasonalOneTimeOffer> => {
	try {
		// 1. First, get the main request details
		const requestResponse = await http.get<RequestDetailResponse>(
			'requests',
			`specialist-request/by-id/${requestId}`
		);

		if (!requestResponse.data.success) {
			throw new Error(requestResponse.data.error || 'Failed to fetch request details');
		}

		const requestData = requestResponse.data.data;

		// 2. Create parallel requests for user, crop, and language data
		const [userResponse, cropsResponse, languagesResponse] = await Promise.all([
			http.get<UserDataResponse>('users', `request-user/${requestData.farmer_id}`),
			http.get<CropDataResponse>('crops', 'list/'),
			http.get<LanguageDataResponse>('languages', 'list/'),
		]);

		// 3. Process the data
		const userData = userResponse.data.data;

		// Find the crop data by crop_id
		const allCrops = cropsResponse.data.data;
		const cropData = allCrops.find((crop) => crop.id === requestData.crop_id) || {
			id: requestData.crop_id,
			name: 'Unknown Crop',
			image_url: '',
			country_code: 'en',
		};

		// Find the language data from language IDs
		const allLanguages = languagesResponse.data.data;
		const languageData = requestData.communication_language.map((langId) => {
			const language = allLanguages.find((lang) => lang.id === langId);
			return language || { id: langId, name: 'Unknown Language' };
		});

		// 4. Transform the data to match SeasonalOneTimeOffer structure
		const seasonalOnetimeOffer: SeasonalOneTimeOffer = {
			id: requestData.id,
			crop: {
				id: cropData.id,
				name: cropData.name,
				image_url: cropData.image_url,
			},
			farmer: {
				id: userData.id,
				user_id: userData.user_id,
				first_name: userData.first_name,
				last_name: userData.last_name,
				full_name: `${userData.first_name} ${userData.last_name}`,
				profile_image_url: userData.profile_image,
				country: {
					name: userData.country,
					alpha_two: userData.unicode?.alpha_two || '',
					alpha_three: userData.unicode?.alpha_three || '',
				},
			},
			sent_date: requestData.created_at,
			request_type:
				requestData.request_type === 'SEASONAL' ? RequestType.SEASONAL : RequestType.ONE_TIME,
			duration_days: '5', // Default or calculate from dates
			duration_timestamp: requestData.end_date,
			request_valid_for_days: '5', // Default or calculate from dates
			request_valid_for_timestamp: requestData.end_date,
			farm_details: {
				area: requestData.area,
				unit: requestData.unit,
				location: {
					address: requestData.location_name,
					latitude: requestData.latitude,
					longitude: requestData.longitude,
				},
			},
			farmer_id: requestData.farmer_id,
			communication_languages: languageData,
			description: requestData.problem_description,
			price: requestData.services?.price && !isNaN(parseFloat(requestData.services.price)) 
				? parseFloat(requestData.services.price) 
				: 0,
			currency: requestData.services?.currency || 'EUR',
			start_date: requestData.start_date,
			end_date: requestData.end_date,
			attachments: requestData.request_files,
			// Calculate if date is still valid to determine if the offer is active
			is_paid: requestData.payment_status === 'paid' || requestData.payment_status === 'stripe_payed',
			payment_status: requestData.payment_status,
			is_sent: requestData.offer_id !== null,
			offer_id: requestData.offer_id || undefined,
			request_id: requestData.request_id, // Add request_id for channelId lookup
		};

		// 5. Get and add channel ID
		try {
			// Import the chatService directly to avoid circular dependencies
			const { chatService } = await import('services/chat/chatService');
			const channelId = await chatService.getChannelIdByRequestAndUserId(
				requestData.request_id,
				userData.user_id
			);
			seasonalOnetimeOffer.channelId = channelId || '';
		} catch (error) {
			console.error(`Error fetching channelId for request ${requestData.request_id}:`, error);
			seasonalOnetimeOffer.channelId = '';
		}

		return seasonalOnetimeOffer;
	} catch (error: any) {
		console.error('Error fetching combined request details:', error);
		throw error;
	}
};

export const sendOffer = async (
	offerId: string,
	offerData: SendOfferRequest
): Promise<{ success: boolean; message: string }> => {
	try {
		// Ensure price is properly formatted and currency is included
		const requestData = {
			...offerData,
			// Convert number to string for the API if needed
			price: offerData.price.toString(),
			// Make sure currency is included
			currency: offerData.currency || 'EUR',
		};

		await http.post('requests', `services-request/offers/${offerId}/send`, requestData);
		return {
			success: true,
			message: 'Offer sent successfully',
		};
	} catch (error: any) {
		console.error('Error sending offer:', error);
		throw error;
	}
};

export const editOffer = async (
	offerId: string,
	offerData: SendOfferRequest
): Promise<{ success: boolean; message: string }> => {
	try {
		// Ensure price is properly formatted and currency is included
		const requestData = {
			...offerData,
			// Convert number to string for the API if needed
			price: offerData.price.toString(),
			// Make sure currency is included
			currency: offerData.currency || 'EUR',
		};

		await http.put('requests', `services-request/offers/${offerId}`, requestData);
		return {
			success: true,
			message: 'Offer updated successfully',
		};
	} catch (error: any) {
		console.error('Error updating offer:', error);
		throw error;
	}
};

export const cancelOffer = async (offerId: string): Promise<OfferDetails> => {
	try {
		const response = await http.delete<{ success: boolean; data: OfferDetails; message?: string }>(
			'offers',
			`cancel-offer/${offerId}`
		);
		return response.data.data;
	} catch (error: any) {
		console.error('Error cancelling offer:', error);
		throw error;
	}
};

export const createOffer = async (
	offerData: CreateOfferRequest
): Promise<{ success: boolean; data: any; message: string }> => {
	try {
		// Ensure the price is a string and currency is passed
		const requestData = {
			...offerData,
			// Make sure currency is included
			currency: offerData.currency || 'EUR',
		};

		const response = await http.post<CreateOfferResponse>('offers', 'create-offer', requestData);

		return {
			success: true,
			data: response.data,
			message: 'Offer created successfully',
		};
	} catch (error: any) {
		console.error('Error creating offer:', error);
		throw error;
	}
};

export const updateOffer = async (
	offerId: string,
	offerData: CreateOfferRequest
): Promise<{ success: boolean; data: any; message: string }> => {
	try {
		// Ensure the price is a string and currency is passed
		const requestData = {
			...offerData,
			// Make sure currency is included
			currency: offerData.currency || 'EUR',
		};

		const response = await http.post<CreateOfferResponse>('offers', 'create-offer', requestData);

		return {
			success: true,
			data: response.data,
			message: 'Offer updated successfully',
		};
	} catch (error: any) {
		console.error('Error updating offer:', error);
		throw error;
	}
};

export const getOfferDetails = async (offerId: string): Promise<OfferDetails> => {
	try {
		const response = await http.get<OfferDetailsResponse>('offers', `get-offer/${offerId}`);

		if (!response.data.success) {
			throw new Error(response.data.error || 'Failed to fetch offer details');
		}

		return response.data.data;
	} catch (error: any) {
		console.error('Error fetching offer details:', error);
		throw error;
	}
};

/**
 * Get the chat channel ID for a specific request and user
 * @param requestId - The ID of the request
 * @param userId - The ID of the user to get the channel with (typically the farmer ID)
 * @returns Promise resolving to the channel ID or empty string if not found
 */
export const getChannelIdByRequestId = async (
	requestId: string,
	userId: string
): Promise<string> => {
	try {
		// Import chatService directly to avoid circular dependencies
		const { chatService } = await import('services/chat/chatService');

		// Use the existing chat service function to get the channel ID
		const channelId = await chatService.getChannelIdByRequestAndUserId(requestId, userId);
		return channelId || '';
	} catch (error: any) {
		console.error('Error fetching channel ID:', error);
		return '';
	}
};

/**
 * Get seasonal/one-time offer details with the chat channel ID included
 * @param requestId - The ID of the request to fetch
 * @returns Promise resolving to the SeasonalOneTimeOffer with channelId filled in
 */
export const getRequestDetailWithChannelId = async (
	requestId: string
): Promise<SeasonalOneTimeOffer> => {
	// Already implemented with channelId in the getRequestDetailById function
	return getRequestDetailById(requestId);
};
